import type { TExcursionFiltersState } from '@/entities/filter/store/types/excursion/state';
import type { TTourFiltersState } from '@/entities/filter/store/types/tour/state';
import type { StartTimeValue } from '@/shared/types/basic';

export const toMindboxDate = (value: string | null): string => {
  if (!value || typeof value !== 'string') {
    return '';
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }
  const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return `${match[1]}-${match[2]}-${match[3]}`;
  }
  const ddmmyy = trimmed.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})$/);
  if (ddmmyy) {
    return `${ddmmyy[3]}-${ddmmyy[2].padStart(2, '0')}-${ddmmyy[1].padStart(2, '0')}`;
  }

  return trimmed;
};

export const toNumber = (value: number | null | undefined): number => {
  if (value == null || Number.isNaN(value)) {
    return 0;
  }

  return Math.floor(Number(value));
};

export const mindboxExcursionNumberOfPeople = (searchState: TExcursionFiltersState): number =>
  toNumber(searchState.date.persons) || 1;

export const mindboxTourNumberOfPeople = (searchState: TTourFiltersState): number => toNumber(searchState.persons) || 1;

export const mapMovementTypes = (
  slugs: string[] | null,
  labelBySlug: Record<string, string | undefined> | null | undefined,
): string[] => {
  if (!slugs?.length) {
    return [];
  }
  const labels = slugs.map((slug) => (labelBySlug && labelBySlug[slug]) || slug).filter(Boolean);

  return [...new Set(labels)];
};

export const mapExcursionFormatTypes = (
  state: Pick<TExcursionFiltersState, 'type' | 'tourFormat'>,
  labelByValue: Record<string, string | undefined> | null | undefined,
): string[] => {
  const types = state.type?.length ? state.type : (state.tourFormat ?? []);
  if (!types.length) {
    return [];
  }
  const labels = types.map((value) => (labelByValue && labelByValue[value]) || value).filter(Boolean);

  return [...new Set(labels)];
};

export const mapTourFormatTypes = (
  state: Pick<TTourFiltersState, 'tourFormat'>,
  labelByValue: Record<string, string | undefined> | null | undefined,
): string[] => {
  const types = state.tourFormat ?? [];
  if (!types.length) {
    return [];
  }
  const labels = types.map((value) => (labelByValue && labelByValue[value]) || value).filter(Boolean);

  return [...new Set(labels)];
};

export const mapDurationInterval = (
  intervalSlugs: string[],
  labelBySlug: Record<string, string | undefined> | null | undefined,
): string[] => {
  if (!intervalSlugs?.length) {
    return [];
  }
  const labels = intervalSlugs.map((slug) => (labelBySlug && labelBySlug[slug]) || slug).filter(Boolean);

  return [...new Set(labels)];
};

export const mapStartTime = (
  intervals: StartTimeValue[],
  labelByValue: Record<string, string | undefined> | null | undefined,
): string[] => {
  if (!intervals?.length) {
    return [];
  }
  const labels = intervals.map((v) => (labelByValue && labelByValue[v]) || v).filter(Boolean);

  return [...new Set(labels)];
};

export const mapSorting = (
  sorting: string | null,
  labelByValue: Record<string, string | undefined> | null | undefined,
): string[] => {
  if (!sorting) {
    return [];
  }
  const label = labelByValue && labelByValue[sorting];

  return label ? [label] : [sorting];
};

export const mapRubrics = (tagSlugs: string | null): string[] => {
  if (!tagSlugs || tagSlugs === 'all') {
    return [];
  }

  return tagSlugs
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
};

export const mapArrayToLabels = (
  values: string[] | null | undefined,
  labelByValue: Record<string, string | undefined> | null | undefined,
): string[] => {
  if (!values?.length) {
    return [];
  }
  const labels = values.map((v) => (labelByValue && labelByValue[v]) || v).filter(Boolean);

  return [...new Set(labels)];
};
